import { engineComponent, NumberUtils, Utils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { PlayList } from "../Components/Sounds/PlayList";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { BetManager } from "../Managers/BetManager";
import { Payline } from "../GameComponents/Payline";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Tweens } from "../Components/Effects/Tweens";
import { ReelExtended } from "./ReelExtended";
import { ModuleManager } from "../Managers/ModuleManager";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { AppContext } from "../../Common/AppContext";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { BaseView } from "../GameComponents/Base/BaseView";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { CustomUtils } from "../Utils/CustomUtils";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { FrameTimerPool } from "../Utils/FrameTimerPool";

type IWin = Communication.Game.IWin;

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    public static Force: boolean = false;

    //******************************************************************************************************************
    //  CONFIG
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    // wins from succes response
    private _wins: IWin[];

    public static progressBarDelay: number;
    public static cherryDelay: number;
    public static lemonDelay: number;
    public static orangeDelay: number;
    public static plumDelay: number;
    public static grapeDelay: number;
    public static melonDelay: number;

    private _textContainerBottom: HTMLElement;
    private _textContainer: HTMLElement;

    public currentWinSymbolsContainer: ContainerElement;
    private _finishFirstWins: FrameTimerEventEmitter;
    private _firstSingleWinsEnd: FrameTimerEventEmitter;
    private _firstWinsEnd: FrameTimerEventEmitter;

    private _updateProgressLemon: FrameTimerEventEmitter;
    private _updateProgressOrange: FrameTimerEventEmitter;
    private _updateProgressPlum: FrameTimerEventEmitter;
    private _updateProgressGrape: FrameTimerEventEmitter;
    private _updateProgressMelon: FrameTimerEventEmitter;

    public showTextUnderLogoDelay: number;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._firstSingleWinsEnd = CreateFrameTimer("firstSingleWinsEnd");
        this._firstSingleWinsEnd.on(FrameTimerEvents.Complete, this.finishFirstWinsSingleEnd.bind(this));

        this._firstWinsEnd = CreateFrameTimer("firstWinsEnd");
        this._firstWinsEnd.on(FrameTimerEvents.Complete, this.finishFirstWinsEnd.bind(this));

        this._finishFirstWins = CreateFrameTimer("finishFirstWins");
        this._finishFirstWins.on(FrameTimerEvents.Complete, this.finishFirstWins.bind(this));

        this._updateProgressLemon = CreateFrameTimer("updateProgressLemon");
        this._updateProgressLemon.on(FrameTimerEvents.Complete, this.updateProgressLemon.bind(this));

        this._updateProgressOrange = CreateFrameTimer("updateProgressOrange");
        this._updateProgressOrange.on(FrameTimerEvents.Complete, this.updateProgressOrange.bind(this));

        this._updateProgressPlum = CreateFrameTimer("updateProgressPlum");
        this._updateProgressPlum.on(FrameTimerEvents.Complete, this.updateProgressPlum.bind(this));

        this._updateProgressGrape = CreateFrameTimer("updateProgressGrape");
        this._updateProgressGrape.on(FrameTimerEvents.Complete, this.updateProgressGrape.bind(this));

        this._updateProgressMelon = CreateFrameTimer("updateProgressMelon");
        this._updateProgressMelon.on(FrameTimerEvents.Complete, this.updateProgressMelon.bind(this));
    }

    public create(node: Element): void {
        super.create(node);

        this._textContainerBottom = document.getElementById("textContainerBottom");
        this._textContainer = document.getElementById("textContainer");
    }

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public onReelStopped(
        reelsData: Array<Communication.Game.IReel>,
        reelIndex: number,
        reelsTransition?: SwitchElement,
    ): void {
        super.onReelStopped(reelsData, reelIndex);

        if (!WinAnimatorExtended.Force) {
            this.impactSpecialSymbols(reelsData, reelIndex);
        }

        if (!ReelExtended.Tease) {
            this.impactBonus(reelsData, reelIndex);
            this.impactWilds(reelsData, reelIndex);
        }

        this.moveToTop();
    }

    private impactSpecialSymbols(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (
            (reelIndex < 3 || ReelExtended.Tease) &&
            reelsData[reelIndex].symbols.indexOf(this.getTeasingSymbol()) !== -1
        ) {
            const count = this.countScatters(reelIndex);
            if (count == 1) {
                PlayList.play("SND_BaseBonusHitFirst" + reelIndex);
            } else if (count == 2) {
                PlayList.play("SND_BaseBonusHitSecond" + reelIndex);
            } else if (count > 2) {
                PlayList.play("SND_BaseBonusHitThird" + reelIndex);
                PlayList.play("SND_BaseBonusWin");
            }
        }

        this.tease(reelIndex);
    }

    protected tease(reelIndex: number): void {
        const count = this.countScatters(reelIndex);

        if (count >= this.getMinTeasingSymbolCount()) {
            this.tease3(reelIndex, count);
        } else if (count === this.getMinTeasingSymbolCount() - 1) {
            this.tease2(reelIndex, count);
        }
    }

    protected countScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (this.currentReels[i].symbols.indexOf(this.getTeasingSymbol()) !== -1) {
                count++;
            }
        }

        return count;
    }

    protected tease2(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            for (let i = 0; i < reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseImpactWithTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseImpactWithTease);
                        const symbol = this.currentSymbols[i][j];
                        this.currentWinSymbolsContainer.addChild(symbol);
                    }
                }
            }

            return;
        }

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id === this.getTeasingSymbol() - 1) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseTease);
                        const symbol = this.currentSymbols[i][j];
                        this.currentWinSymbolsContainer.addChild(symbol);
                    }
                } else {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        this.tease3(reelIndex, count);
    }

    protected tease3(reelIndex: number, count: number): void {
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            if (this.currentSymbols[reelIndex][i].id === this.getTeasingSymbol() - 1) {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseImpactWithTease)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpactWithTease);
                    const symbol = this.currentSymbols[reelIndex][i];
                    this.currentWinSymbolsContainer.addChild(symbol);
                }
            } else {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseNowin)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
                }
            }
        }
    }

    protected getTeasingSymbol(): number {
        return CustomUtils.SymbolBonus;
    }

    protected getMinTeasingSymbolCount(): number {
        return 3;
    }

    protected impactBonus(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                if (
                    reelsData[reelIndex].symbols[i] === CustomUtils.SymbolBonus &&
                    this.currentSymbols[reelIndex][i] != null
                ) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpact);
                    const count = this.countScatters(reelIndex);

                    if (count == 1) {
                        PlayList.play("SND_BaseBonusHitFirst" + reelIndex);
                    } else if (count == 2) {
                        PlayList.play("SND_BaseBonusHitSecond" + reelIndex);
                    } else if (count > 2) {
                        PlayList.play("SND_BaseBonusHitThird" + reelIndex);
                        PlayList.play("SND_BaseBonusWin");
                    }
                }
            }
        }
    }

    protected impactWilds(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                if (
                    reelsData[reelIndex].symbols[i] === CustomUtils.SymbolWild &&
                    this.currentSymbols[reelIndex][i] != null
                ) {
                    PlayList.play("SND_BaseWildHit" + reelIndex);
                }
            }
        }
    }

    private calculatePreviousStars(
        reelsData: Array<Communication.Game.IReel>,
        reelIndex: number,
        symbolId: number,
    ): number {
        let count: number = 0;
        for (let j: number = 0; j < reelIndex; j++) {
            if (reelsData[j] != null && reelsData[j].symbols != null) {
                for (let i: number = 0; i < reelsData[j].symbols.length; i++) {
                    if (reelsData[j].symbols[i] === symbolId) {
                        count++;
                    }
                }
            }
        }

        return count;
    }

    protected finishFirstWins(): void {
        this._finishFirstWins.stop();

        if (this._textContainerBottom != null) {
            this._textContainerBottom.classList.remove(HtmlController.DisabledClass);
        }

        if (this._textContainer != null) {
            this._textContainer.classList.remove(HtmlController.DisabledClass);
        }

        if (this.currentWins && this.currentWins.length > 0) {
            //if scatter win, we get 2 wins (cash, fs) - still single win
            if (this.currentWins[0].trigger == Communication.Game.WinTrigger.Scatter && this.currentWins.length == 2) {
                this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                PlayList.play("SND_BaseRecapWinline");

                if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                    PlayList.loop("SND_BaseRecapLoop");
                    PlayList.loop("SND_BaseBonusIdle");
                }

                return;
            }

            WinAnimatorExtended.progressBarDelay = 0;
            WinAnimatorExtended.cherryDelay = 0;
            WinAnimatorExtended.lemonDelay = 0;
            WinAnimatorExtended.orangeDelay = 0;
            WinAnimatorExtended.plumDelay = 0;
            WinAnimatorExtended.grapeDelay = 0;
            WinAnimatorExtended.melonDelay = 0;

            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].payoutId == CustomUtils.SymbolCherry) {
                    BaseView._isMultipliedTwiceS1 = false;
                    this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                    this.winPaylineGoToCase(i, Payline.CaseNowin);
                    WinAnimatorExtended.cherryDelay = 90;
                    BaseView.updateSpecificProgressBarsAndMultipliers(
                        BaseView.customCollectionBarsData,
                        CustomUtils.SymbolCherry,
                    );
                    if (BaseView._isMultipliedTwiceS1) {
                        WinAnimatorExtended.cherryDelay = 180;
                    }
                }
            }

            this._updateProgressLemon.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.cherryDelay);
        }
    }

    protected finishFirstWinsSingleEnd(): void {
        this._firstSingleWinsEnd.stop();
        this.emit(WinAnimator.FinishWinAnimator);

        // save custom data from previous spin
        BaseView.customCollectionBarsDataPreviousSpin = BaseView.customCollectionBarsData.collectionBars;

        if (this.currentWins != null) {
            this.winSymbolsGoToCase(0, Symbol.CaseSingleWin, true);
            this.winPaylineGoToCase(0, Payline.CaseWin);

            PlayList.play("SND_BaseRecapWinline");
        }

        /*if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
            PlayList.loop('SND_BaseRecapLoop');
            PlayList.loop('SND_BaseBonusIdle');

        }*/
        return;
    }

    protected updateProgressLemon(): void {
        this._updateProgressLemon.stop();
        BaseView._isMultipliedTwiceS2 = false;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].payoutId == CustomUtils.SymbolLemon) {
                this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                this.winPaylineGoToCase(i, Payline.CaseNowin);
                WinAnimatorExtended.lemonDelay = 90;
                BaseView.updateSpecificProgressBarsAndMultipliers(
                    BaseView.customCollectionBarsData,
                    CustomUtils.SymbolLemon,
                );
                if (BaseView._isMultipliedTwiceS2) {
                    WinAnimatorExtended.lemonDelay = 180;
                }
            }
        }

        this._updateProgressOrange.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.lemonDelay);
    }

    protected updateProgressOrange(): void {
        this._updateProgressOrange.stop();
        BaseView._isMultipliedTwiceS3 = false;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].payoutId == CustomUtils.SymbolOrange) {
                this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                this.winPaylineGoToCase(i, Payline.CaseNowin);
                WinAnimatorExtended.orangeDelay = 90;
                BaseView.updateSpecificProgressBarsAndMultipliers(
                    BaseView.customCollectionBarsData,
                    CustomUtils.SymbolOrange,
                );
                if (BaseView._isMultipliedTwiceS3) {
                    WinAnimatorExtended.orangeDelay = 180;
                }
            }
        }

        this._updateProgressPlum.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.orangeDelay);
    }

    protected updateProgressPlum(): void {
        this._updateProgressPlum.stop();
        BaseView._isMultipliedTwiceS4 = false;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].payoutId == CustomUtils.SymbolPlum) {
                this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                this.winPaylineGoToCase(i, Payline.CaseNowin);
                WinAnimatorExtended.plumDelay = 90;
                BaseView.updateSpecificProgressBarsAndMultipliers(
                    BaseView.customCollectionBarsData,
                    CustomUtils.SymbolPlum,
                );
                if (BaseView._isMultipliedTwiceS4) {
                    WinAnimatorExtended.plumDelay = 180;
                }
            }
        }

        this._updateProgressGrape.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.plumDelay);
    }

    protected updateProgressGrape(): void {
        this._updateProgressGrape.stop();
        BaseView._isMultipliedTwiceS5 = false;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].payoutId == CustomUtils.SymbolGrape) {
                this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                this.winPaylineGoToCase(i, Payline.CaseNowin);
                WinAnimatorExtended.grapeDelay = 90;
                BaseView.updateSpecificProgressBarsAndMultipliers(
                    BaseView.customCollectionBarsData,
                    CustomUtils.SymbolGrape,
                );
                if (BaseView._isMultipliedTwiceS5) {
                    WinAnimatorExtended.grapeDelay = 180;
                }
            }
        }

        this._updateProgressMelon.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.grapeDelay);
    }

    protected updateProgressMelon(): void {
        this._updateProgressMelon.stop();
        BaseView._isMultipliedTwiceS6 = false;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].payoutId == CustomUtils.SymbolWatermelon) {
                this.winSymbolsGoToCase(i, Symbol.CasePop, false);
                this.winPaylineGoToCase(i, Payline.CaseNowin);
                WinAnimatorExtended.melonDelay = 90;
                BaseView.updateSpecificProgressBarsAndMultipliers(
                    BaseView.customCollectionBarsData,
                    CustomUtils.SymbolWatermelon,
                );
                if (BaseView._isMultipliedTwiceS6) {
                    WinAnimatorExtended.melonDelay = 180;
                }
            }
        }

        this._firstWinsEnd.start(WinAnimatorExtended.progressBarDelay + WinAnimatorExtended.melonDelay);
    }

    public stopProgressbarsTimers() {
        this._updateProgressLemon.stop();
        this._updateProgressOrange.stop();
        this._updateProgressPlum.stop();
        this._updateProgressGrape.stop();
        this._updateProgressMelon.stop();
    }

    protected finishFirstWinsEnd(): void {
        this._firstWinsEnd.stop();
        this.stopTimer();
        this.emit(WinAnimator.FinishWinAnimator);

        // save custom data from previous spin
        const customData = BaseView.customCollectionBarsData;
        BaseView.customCollectionBarsDataPreviousSpin = customData.collectionBars;

        this.currentCycle = 0;
        this.cycleWins(true);
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();
        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
            this.dynamicTint();
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
        this.moveToTop();
    }

    protected showFirstWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        let scatterWin = 0;

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);

            if (this.isScatterWin(this.currentWins[i])) {
                scatterWin += this.currentWins[i].amount;
            }
        }

        this.currentWin -= scatterWin;
        if (this.currentWin > 0) {
            this.startAnimation();
        } else {
            this._finishFirstWins.start(60);
        }
    }

    protected startAnimation(): void {
        super.startAnimation();

        clearTimeout(this.showTextUnderLogoDelay);

        if (this.currentAnimationIndex >= 3) {
            if (this._textContainerBottom != null) {
                this._textContainerBottom.classList.add(HtmlController.DisabledClass);
            }

            if (this._textContainer != null) {
                this._textContainer.classList.add(HtmlController.DisabledClass);
            }
        }
    }

    protected stopAnimation(): void {
        super.stopAnimation();

        if (!ModuleManager.gambleActive() && !ModuleManager.freespinsActive()) {
            this.showTextUnderLogoDelay = setTimeout(() => {
                if (!FrameTimerPool.paused) {
                    if (
                        this._textContainerBottom != null &&
                        this._textContainerBottom.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._textContainerBottom.classList.remove(HtmlController.DisabledClass);
                    }

                    if (
                        this._textContainer != null &&
                        this._textContainer.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._textContainer.classList.remove(HtmlController.DisabledClass);
                    }
                }
            }, 60 * 16.6667);
        }
    }

    protected isScatterWin(currentWin: Communication.Game.IWin): boolean {
        if (currentWin.trigger == Communication.Game.WinTrigger.Scatter) {
            return true;
        }

        return false;
    }

    protected hasScatterWin(currentWins: Array<Communication.Game.IWin>): boolean {
        for (let i = 0; i < currentWins.length; i++) {
            if (this.isScatterWin(currentWins[i])) {
                return true;
            }
        }

        return false;
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }
            this.moveToTop();
            return this.currentMultiplierIndex >= 3;
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNormal, false);
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            this.moveToTop();
            return false;
        }
    }

    public dynamicTint(id: number = null): void {
        let winId: number = id;
        if (winId == null) {
            let highestWin: IWin = null;

            if (!Utils.isNullOrUndefined(this._wins)) {
                for (let i: number = 0; i < this._wins.length; i++) {
                    if (highestWin == null) {
                        highestWin = this._wins[i];
                    } else {
                        let nextWin: IWin = this._wins[i];
                        if (nextWin.amount > highestWin.amount) {
                            highestWin = nextWin;
                        }
                    }
                }
                if (highestWin != null && highestWin.payoutId != null) {
                    winId = highestWin.payoutId;
                }
            }
        }

        if (winId != null) {
            switch (winId) {
                case 1:
                    ImageElement.DynamicTint = "ff005a";
                    AnimationElement.DynamicTint = "ff005a";
                    break;
                case 2:
                    ImageElement.DynamicTint = "ffe903";
                    AnimationElement.DynamicTint = "ffe903";
                    break;
                case 3:
                    ImageElement.DynamicTint = "ff5a00";
                    AnimationElement.DynamicTint = "ff5a00";
                    break;
                case 4:
                    ImageElement.DynamicTint = "0018ff";
                    AnimationElement.DynamicTint = "0018ff";
                    break;
                case 5:
                    ImageElement.DynamicTint = "fc00ff";
                    AnimationElement.DynamicTint = "fc00ff";
                    break;
                case 6:
                    ImageElement.DynamicTint = "18e00e";
                    AnimationElement.DynamicTint = "18e00e";
                    break;
                case 7:
                    ImageElement.DynamicTint = "ffff00";
                    AnimationElement.DynamicTint = "ffff00";
                    break;
                default:
                    ImageElement.DynamicTint = "ff0000";
                    AnimationElement.DynamicTint = "ff0000";
            }
        }
    }

    private setDefaultDynamicTint(): void {
        ImageElement.DynamicTint = "ffff00";
        AnimationElement.DynamicTint = "ffff00";
    }

    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin + "_END", false);
            } else {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            }

            this.winPaylineGoToCase(i, Payline.CaseWin);
        }
    }

    protected finishFirstInitWins(): void {
        if (this.currentWins != null) {
            if (this.currentWins.length == 1) {
                this.winSymbolsGoToCase(0, Symbol.CaseSingleWin, true);
                this.winPaylineGoToCase(0, Payline.CaseWin);
                PlayList.play("SND_BaseRecapWinline");

                if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                    PlayList.loop("SND_BaseRecapLoop");
                    PlayList.loop("SND_BaseBonusIdle");
                }
                return;
            } else if (this.currentWins.length > 1) {
                //if scatter win, we get 2 wins (cash, fs) - still single win
                if (
                    this.currentWins[0].trigger == Communication.Game.WinTrigger.Scatter &&
                    this.currentWins.length == 2
                ) {
                    this.winSymbolsGoToCase(0, Symbol.CaseWin, true);
                    PlayList.play("SND_BaseRecapWinline");

                    if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive()) {
                        PlayList.loop("SND_BaseRecapLoop");
                        PlayList.loop("SND_BaseBonusIdle");
                    }

                    return;
                }

                this.stopTimer();
                this.currentCycle = 0;
                this.cycleWins(true);
            }
        }
    }

    protected cycleWins(isFirst: boolean = false): void {
        if (this.currentWins == null) {
            return;
        }

        //skip freespins win cycle win
        if (
            this.currentWins.length == 1 &&
            this.currentWins[this.currentCycle].type === Communication.Game.WinType.freeSpins
        ) {
            return;
        }

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }

        if (isFirst) {
            this.cycleStep = 0;
        } else {
            this.cycleStep++;
        }

        PlayList.play("SND_BaseRecapWinline");

        if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive() && this.cycleStep % 4 === 0) {
            this.cycleStep = 0;
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.stop("SND_BaseBonusIdle");
            PlayList.loop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseBonusIdle");
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseCycleNonActive);
        }

        this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
        this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelayWithCorrection(isFirst));
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];
        this.dynamicTint(win.payoutId);

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];

                    if (symbol == null) {
                    } else {
                        if (caseType === Symbol.CaseCycle) {
                            symbol.goToCase(Symbol.CaseCycle + "_" + i);
                        } else {
                            symbol.goToCase(caseType);
                        }

                        if (
                            (caseType == Symbol.CaseWin ||
                                caseType == Symbol.CaseCycle ||
                                caseType == Symbol.CaseSingleWin) &&
                            this.currentWinSymbolsContainer != null
                        ) {
                            this.currentWinSymbolsContainer.addChild(symbol);
                        } else {
                            this.symbolsContainer.addChild(symbol);
                        }

                        symbols.push(symbol);
                    }
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol != null) {
                        if (symbol.getCurrentCase() != Symbol.CaseWin + "_END") {
                            if (caseType === Symbol.CaseCycle) {
                                symbol.goToCase(Symbol.CaseCycle + "_" + i);
                            } else {
                                symbol.goToCase(caseType);
                            }
                        }
                        this.symbolsContainer.addChild(symbol);
                        symbols.push(symbol);
                    }
                }
            }
        }

        return symbols;
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        if (this.currentSymbols[reelIndex][symbolIndex] != null) {
            if (
                (this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle ||
                    this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle + "_2" ||
                    this.currentSymbols[reelIndex][symbolIndex].getCurrentCase() === Symbol.CaseIdle + "_3") &&
                caseType === Symbol.CaseNormal
            ) {
                return false;
            }

            this.moveToTop();

            return true;
        }

        return true;
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);

        this.moveToTop();
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): void {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (useValue && symbols.length > 0 && win.amount > 0) {
            this.value.visible = true;
            this.value.position.x = symbols[0].position.x + symbols[0].offsetX;
            this.value.position.y = symbols[0].position.y + symbols[0].offsetY;
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        this.moveToTop();
    }

    protected scaleSymbols(
        symbol: Symbol,
        tweenValuesOrig: Array<number>,
        tweenDuration: Array<number>,
        equal: boolean,
    ) {
        let container: ContainerElement = <any>symbol.children[0];
        Tweens.removeTweens(container);
        let chooseIf: boolean;
        if (equal) {
            chooseIf = container.scale.x == 1;
        } else {
            chooseIf = container.scale.x != 1;
        }

        if (chooseIf) {
            let tween: string = "scale.x";
            let tweenVal: Array<number> = tweenValuesOrig.slice(0);
            let tweenValues: Array<number> = tweenValuesOrig;
            let tweenEase: Array<string> = [];
            let tweenDelay: Array<number> = [0];
            let tweenLoop: boolean = false;

            container.pivot.x = this.symbolWidth / 2;
            container.pivot.y = this.symbolHeight / 2;
            container.position.x = this.symbolWidth / 2;
            container.position.y = this.symbolHeight / 2;
            Tweens.customTween(container, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            tween = "scale.y";
            tweenValues = tweenVal;
            Tweens.customTween(container, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
        }
    }

    public reset(showLastSymbols: boolean): boolean {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j] != null) {
                    let symbol: Symbol = <any>this.currentSymbols[i][j];
                    let container: ContainerElement = <any>symbol.children[0];
                    container.pivot.x = 0;
                    container.pivot.y = 0;
                    container.position.x = 0;
                    container.position.y = 0;
                    container.scale.x = 1;
                    container.scale.y = 1;
                    Tweens.removeTweens(container);
                }
            }
        }

        return super.reset(showLastSymbols);
    }

    protected getIdleDelay(): number {
        return 30;
    }

    protected getInitialIdleDelay(): number {
        return 30;
    }

    protected getIdleActiveElementsCount(): number {
        return 2;
    }

    protected moveToTop(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && (symbol.id === 6 || symbol.id === 7)) {
                    symbol.parent.addChild(symbol);
                }
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/